import { useFieldArray, useFormContext } from 'react-hook-form';

import { Tooltip } from 'primereact/tooltip';
import PropTypes from 'prop-types';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { classNames } from 'primereact/utils';
import WorkHistoryDialog from './WorkHistoryDialog';

export default function WorkHistoryForm({ control, shouldUpdateFromDocx }) {
  // #region Data
  const { t } = useTranslation();
  const { dirtyFields } = useFormContext(); // retrieve all hook methods

  const workHistoryRef = useRef();

  const { fields, insert, update, remove } = useFieldArray({
    control,
    name: 'workHistory',
  });
  // #endregion

  // #region Event
  // #endregion
  return (
    <>
      <h4 className="text-center mb-4">{t('scientificProfile.workHistory.label')}</h4>

      <div className="form-table mt-2">
        <table className="mb-2">
          <thead>
            <tr>
              <th className="text-center" style={{ minWidth: 50, width: 50 }}>
                {t('formLayout.numericalOrder')}
              </th>
              <th className="text-center" style={{ minWidth: 200, width: '20%' }}>
                {t('scientificProfile.workHistory.timePeriod')}
              </th>
              <th className="text-center" style={{ minWidth: 100, width: '40%' }}>
                {t('scientificProfile.workHistory.address')}
              </th>
              <th className="text-center" style={{ minWidth: 100, width: '30%' }}>
                {t('scientificProfile.workHistory.position')}
              </th>
              <th className="text-center float" style={{ minWidth: 80, width: 80 }}>
                {t('formLayout.action.label')}
              </th>
            </tr>
          </thead>
          <tbody>
            {fields?.map((item, index) => (
              <tr
                key={item.id ?? index}
                className={classNames({
                  highlighted: shouldUpdateFromDocx && dirtyFields.workHistory,
                })}
              >
                <td className="text-center">{index + 1}</td>
                <td className="text-center">
                  {item?.start ? new Date(item?.start).toLocaleDateString('en-GB') : ''}
                  {item?.end ? ` - ${new Date(item?.end).toLocaleDateString('en-GB')}` : ` ${t('scientificProfile.workHistory.toDate')}`}
                </td>
                <td>{item?.address}</td>
                <td>{item?.position}</td>

                <td
                  className={`flex flex-column float text-center p-0 border-none ${
                    index !== 0 ? 'border-top-1' : ''
                  }`}
                >
                  <i
                    onClick={() => {
                      workHistoryRef.current.open({
                        insert,
                        index: index + 1,
                        type: 'INSERT',
                      });
                    }}
                    className="insert-icon pi pi-plus text-primary cursor-pointer my-1"
                    data-pr-tooltip={t('formLayout.action.insert')}
                    data-pr-position="left"
                  />
                  <Tooltip target=".insert-icon" />

                  <i
                    onClick={() => {
                      workHistoryRef.current.open({
                        update,
                        index,
                        value: fields[index],
                        type: 'UPDATE',
                      });
                    }}
                    className="edit-icon pi pi-pencil text-primary cursor-pointer mb-1"
                    data-pr-tooltip={t('formLayout.action.edit')}
                    data-pr-position="left"
                  />
                  <Tooltip target=".edit-icon" />

                  <i
                    onClick={() => remove(index)}
                    className="remove-icon pi pi-times text-red-700 cursor-pointer mb-1"
                    data-pr-tooltip={t('formLayout.action.remove')}
                    data-pr-position="left"
                  />
                  <Tooltip target=".remove-icon" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {fields?.length === 0 && (
          <div className="flex justify-content-center my-2">
            <i
              onClick={() => {
                workHistoryRef.current.open({
                  insert,
                  index: 0,
                  type: 'INSERT',
                });
              }}
              className="add-work-history pi pi-plus text-primary cursor-pointer bg-primary p-1 border-circle"
              data-pr-tooltip={t('formLayout.action.add')}
              data-pr-position="left"
            />
            <Tooltip target=".add-work-history" />
          </div>
        )}
      </div>
      <WorkHistoryDialog ref={workHistoryRef} />
    </>
  );
}

WorkHistoryForm.propTypes = {
  control: PropTypes.shape({}).isRequired,
  errors: PropTypes.shape({}).isRequired,
  shouldUpdateFromDocx: PropTypes.bool,
};

WorkHistoryForm.defaultProps = {
  shouldUpdateFromDocx: false,
};
